import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchBanks,
  createBank,
  updateBank,
  deleteBank,
} from "../../api/chequeMasterApi";
import { getCsrfToken } from "../../../../public/api/authApi";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import { GlobalModal } from "../../../../global/GlobalModal";
import {
  GlobalTextField,
  GlobalButton,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import GlobalFeedback from "../../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";
import GlobalBadge from "../../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import { useChequeRole } from "../../utils/useChequeRole";
import { CHEQUE_ISSUES_SUBMODULE_SLUG } from "../../utils/chequePermissionSlugs";

const STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "2", label: "Inactive" },
];

const BankMaster = ({ onMastersChange }) => {
  const perms = useChequeRole(CHEQUE_ISSUES_SUBMODULE_SLUG);
  const canManage = !perms.permissionsLoading && perms.canManageMasters;
  const canDelete = !perms.permissionsLoading && perms.canDeleteMasters;
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    branch_name: "",
    status_id: "",
  });
  const [formError, setFormError] = useState("");
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);

  const scrollToForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  const emptyForm = () => ({
    id: null,
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    branch_name: "",
    status_id: "1",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchBanks();
      setDataList(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const setFormField = (name, value) => {
    let next = value;
    if (name === "ifsc_code") {
      next = String(value ?? "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [name]: next }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError("");
  };

  const openCreate = () => {
    setEditing(false);
    setFormData(emptyForm());
    setFormError("");
    setFormErrorDetails({});
    setSuccessMsg("");
    scrollToForm();
  };

  const openEdit = (item) => {
    setSuccessMsg("");
    setEditing(true);
    setFormData({
      id: item.id,
      bank_name: item.bank_name,
      account_number: item.account_number || "",
      ifsc_code: String(item.ifsc_code ?? "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toUpperCase(),
      branch_name: item.branch_name || "",
      status_id:
        item.status_id != null && item.status_id !== ""
          ? String(item.status_id)
          : "",
    });
    setFormError("");
    setFormErrorDetails({});
    scrollToForm();
  };

  const handleCancel = () => {
    if (submitting) return;
    setEditing(false);
    setFormData(emptyForm());
    setFormError("");
    setFormErrorDetails({});
  };

  const resetAfterSave = () => {
    setEditing(false);
    setFormData(emptyForm());
    setFormError("");
    setFormErrorDetails({});
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError("");
    setFormErrorDetails({});
    setSuccessMsg("");

    const errs = {};
    const bName = formData.bank_name.trim();
    if (!bName) {
      errs.bank_name = "Bank name is required.";
    } else if (!/^[A-Za-z\s]+$/.test(bName)) {
      errs.bank_name = "Bank name must be alphabetic only.";
    }

    if (!formData.account_number || !formData.account_number.trim()) {
      errs.account_number = "Account number is required.";
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.account_number.trim())) {
      errs.account_number = "Account number must be alphanumeric.";
    }

    const ifscTrim = formData.ifsc_code.trim();
    if (!ifscTrim) {
      errs.ifsc_code = "IFSC code is required.";
    } else if (!/^[A-Z0-9]+$/.test(ifscTrim)) {
      errs.ifsc_code = "IFSC must be uppercase letters and digits only.";
    }

    if (!formData.branch_name || !formData.branch_name.trim()) {
      errs.branch_name = "Branch name is required.";
    } else if (!/^[A-Za-z\s]+$/.test(formData.branch_name.trim())) {
      errs.branch_name = "Branch name must be alphabetic only.";
    }

    if (Object.keys(errs).length > 0) {
      setFormError("Please correct the fields below.");
      setFormErrorDetails(errs);
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }
    setSubmitting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      if (editing) {
        const sid =
          formData.status_id === "" ? null : Number(formData.status_id);
        await updateBank(
          formData.id,
          {
            bank_name: formData.bank_name.trim(),
            account_number: formData.account_number || null,
            ifsc_code: formData.ifsc_code.trim() || null,
            branch_name: formData.branch_name || null,
            status_id: sid,
          },
          csrfToken,
        );
      } else {
        const sid = formData.status_id === "" ? null : Number(formData.status_id);
        await createBank(
          {
            bank_name: formData.bank_name.trim(),
            account_number: formData.account_number || null,
            ifsc_code: formData.ifsc_code.trim() || null,
            branch_name: formData.branch_name || null,
            status_id: sid,
          },
          csrfToken,
        );
      }
      setSuccessMsg(
        editing ? "Bank updated successfully." : "Bank added successfully.",
      );
      resetAfterSave();
      loadData();
      onMastersChange?.();
    } catch (err) {
      setFormError(err.message);
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteBank(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Bank removed from the directory.");
      loadData();
      onMastersChange?.();
    } catch (err) {
      setError(err.message || "Failed to remove bank");
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const ALERT_ID = "ci-bank-master-alert";

  if (loading) {
    return <p className="ub-loading">Loading banks…</p>;
  }

  return (
    <div className="ub-master-section">
      <h3 className="ub-section__subhead" style={{ margin: "0 0 1.25rem" }}>
        Bank master
      </h3>
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <GlobalFeedback type="error" text={error} />

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        {canManage ? (
        <GlobalFormShell
          legend={editing ? "Edit bank" : "Add bank"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {editing ? (
                <GlobalButton
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton
                variant="primary"
                onClick={handleSubmit}
                loading={submitting}
              >
                {editing ? "Save changes" : "Add bank"}
              </GlobalButton>
            </>
          }
        >
          {editing ? (
            <>
              <GlobalTextField
                id="ochq_bank_name_ro"
                label="Bank Name"
                value={formData.bank_name}
                onChange={(v) => setFormField("bank_name", v)}
                required
                error={formErrorDetails.bank_name}
                baseAlertId={ALERT_ID}
                placeholder="e.g. State Bank of India"
              />
              <GlobalTextField
                id="ochq_acct_ro"
                label="Account Number"
                value={formData.account_number}
                onChange={(v) => setFormField("account_number", v)}
                required
                error={formErrorDetails.account_number}
                baseAlertId={ALERT_ID}
                placeholder="Digits or alphanumeric, no spaces"
              />
              <GlobalTextField
                id="ochq_ifsc_ro"
                label="IFSC Code"
                value={formData.ifsc_code}
                onChange={(v) => setFormField("ifsc_code", v)}
                required
                error={formErrorDetails.ifsc_code}
                baseAlertId={ALERT_ID}
                placeholder="e.g. SBIN0001234 (typed as capitals)"
                autoComplete="off"
              />
              <GlobalTextField
                id="ochq_branch_ro"
                label="Branch Name"
                value={formData.branch_name}
                onChange={(v) => setFormField("branch_name", v)}
                required
                error={formErrorDetails.branch_name}
                baseAlertId={ALERT_ID}
                placeholder="e.g. Main branch, Anna Nagar"
              />
              <GlobalSelectField
                id="ochq_bank_status_id"
                label="Status"
                value={formData.status_id === "" ? "" : String(formData.status_id)}
                onChange={(v) => setFormField("status_id", v)}
                options={STATUS_OPTIONS}
                error={formErrorDetails.status_id}
                baseAlertId={ALERT_ID}
                placeholderOption="Select active or inactive…"
              />
            </>
          ) : (
            <>
              <GlobalTextField
                id="ochq_bank_name"
                label="Bank Name"
                value={formData.bank_name}
                onChange={(v) => setFormField("bank_name", v)}
                required
                error={formErrorDetails.bank_name}
                baseAlertId={ALERT_ID}
                placeholder="e.g. State Bank of India"
              />
              <GlobalTextField
                id="ochq_acct"
                label="Account Number"
                value={formData.account_number}
                onChange={(v) => setFormField("account_number", v)}
                required
                error={formErrorDetails.account_number}
                baseAlertId={ALERT_ID}
                placeholder="Digits or alphanumeric, no spaces"
              />
              <GlobalTextField
                id="ochq_ifsc"
                label="IFSC Code"
                value={formData.ifsc_code}
                onChange={(v) => setFormField("ifsc_code", v)}
                required
                error={formErrorDetails.ifsc_code}
                baseAlertId={ALERT_ID}
                placeholder="e.g. SBIN0001234 (typed as capitals)"
                autoComplete="off"
              />
              <GlobalTextField
                id="ochq_branch"
                label="Branch Name"
                value={formData.branch_name}
                onChange={(v) => setFormField("branch_name", v)}
                required
                error={formErrorDetails.branch_name}
                baseAlertId={ALERT_ID}
                placeholder="e.g. Main branch, Anna Nagar"
              />
              <GlobalSelectField
                id="ochq_bank_status_id_new"
                label="Status"
                value={formData.status_id === "" ? "" : String(formData.status_id)}
                onChange={(v) => setFormField("status_id", v)}
                options={STATUS_OPTIONS}
                baseAlertId={ALERT_ID}
                placeholderOption="Select active or inactive (optional)…"
              />
            </>
          )}
        </GlobalFormShell>
        ) : (
          <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
            You have view-only access to bank master records.
          </p>
        )}
      </div>

      <GlobalDataTable
          headers={[
            { key: "bank_name", label: "Bank Name" },
            { key: "account_number", label: "Account No." },
            { key: "ifsc_code", label: "IFSC" },
            { key: "branch_name", label: "Branch" },
            { key: "bank_status", label: "Status" },
            { key: "actions", label: "Actions", className: "ub-table__col-actions" },
          ]}
          tableAriaLabel="Bank master table"
          wrapClassName="ub-table-wrap"
          tableClassName="ub-table"
        >
          {dataList.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}
              >
                No bank records found.
              </td>
            </tr>
          ) : (
            dataList.map((item) => (
              <tr key={item.id}>
                <td style={{ fontWeight: 600 }}>{item.bank_name}</td>
                <td>{item.account_number || "—"}</td>
                <td>{item.ifsc_code || "—"}</td>
                <td>{item.branch_name || "—"}</td>
                <td>
                  <GlobalBadge active={Number(item.status_id) === 1} />
                </td>
                <td className="ub-table__col-actions">
                  {canManage ? (
                  <div className="ub-table-actions">
                    <GlobalTableActionButton action="edit" onClick={() => openEdit(item)} />
                    {canDelete ? (
                    <GlobalTableActionButton
                      action="delete"
                      onClick={() => {
                        setItemToDelete(item);
                        setShowDeleteConfirm(true);
                      }}
                    />
                    ) : null}
                  </div>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              variant="ghost"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove bank <b>{itemToDelete?.bank_name}</b> from the directory? It will be
          hidden from lists.
        </p>
      </GlobalModal>
    </div>
  );
};

export default BankMaster;
